import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { showApiErrorToast } from '@/utils/errors';
import {
  confirmDonationBatch,
  createDonationBudget,
  createDonationRequisition,
  createMarketProject,
  disburseDonationBatch,
  publishDonationBatch,
  updateDonationBudget,
  updateDonationRequisition,
  updateMarketProject,
} from '../api/marketPromotionP2.api';
import type {
  DonationBudgetPayload,
  DonationRequisitionPayload,
  MarketProjectPayload,
  PublishDonationPayload,
  UpdateDonationBudgetPayload,
  UpdateMarketProjectPayload,
} from '../types/marketPromotionP2.types';
import { useInvalidateMarketPromotion } from './useInvalidateMarketPromotion';

type SaveMarketProjectVariables =
  | { id: null; payload: MarketProjectPayload }
  | { id: number; payload: UpdateMarketProjectPayload };

export function useSaveMarketProject(onSuccess?: () => void) {
  const invalidate = useInvalidateMarketPromotion();

  return useMutation({
    mutationFn: (variables: SaveMarketProjectVariables) =>
      variables.id === null
        ? createMarketProject(variables.payload)
        : updateMarketProject(variables.id, variables.payload),
    onSuccess: (_data, variables) => {
      invalidate();
      toast.success(variables.id === null ? 'Project added' : 'Project updated');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to save the project'),
  });
}

type SaveDonationBudgetVariables =
  | { id: null; payload: DonationBudgetPayload }
  | { id: number; payload: UpdateDonationBudgetPayload };

export function useSaveDonationBudget(onSuccess?: () => void) {
  const invalidate = useInvalidateMarketPromotion();

  return useMutation({
    mutationFn: (variables: SaveDonationBudgetVariables) =>
      variables.id === null
        ? createDonationBudget(variables.payload)
        : updateDonationBudget(variables.id, variables.payload),
    onSuccess: (_data, variables) => {
      invalidate();
      toast.success(variables.id === null ? 'Budget added' : 'Budget updated');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to save the budget'),
  });
}

export function useSaveDonationRequisition(onSuccess?: () => void) {
  const invalidate = useInvalidateMarketPromotion();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number | null; payload: DonationRequisitionPayload }) =>
      id === null ? createDonationRequisition(payload) : updateDonationRequisition(id, payload),
    onSuccess: (_data, variables) => {
      invalidate();
      toast.success(variables.id === null ? 'Requisition raised' : 'Requisition updated');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to save the requisition'),
  });
}

export function usePublishDonationBatch(onSuccess?: () => void) {
  const invalidate = useInvalidateMarketPromotion();

  return useMutation({
    mutationFn: (payload: PublishDonationPayload) => publishDonationBatch(payload),
    onSuccess: () => {
      invalidate();
      toast.success('Batch published');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to publish'),
  });
}

export function useConfirmDonationBatch(onSuccess?: () => void) {
  const invalidate = useInvalidateMarketPromotion();

  return useMutation({
    mutationFn: ({
      batchId,
      isConfirm,
      comment,
    }: {
      batchId: number;
      isConfirm: boolean;
      comment?: string;
    }) => confirmDonationBatch(batchId, isConfirm, comment),
    onSuccess: (_data, variables) => {
      invalidate();
      toast.success(variables.isConfirm ? 'Batch confirmed' : 'Batch sent back');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to record the decision'),
  });
}

export function useDisburseDonationBatch(onSuccess?: () => void) {
  const invalidate = useInvalidateMarketPromotion();

  return useMutation({
    mutationFn: ({ batchId, comment }: { batchId: number; comment: string }) =>
      disburseDonationBatch(batchId, comment),
    onSuccess: () => {
      invalidate();
      toast.success('Batch disbursed');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to disburse'),
  });
}
